import React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Group,
  NumberInput,
  Select,
  Stack,
  TextInput,
  Textarea,
  Paper,
  ActionIcon,
  Title,
  Box,
} from '@mantine/core';
import { useProposal } from '../../context/ProposalContext';
import AddFee from './AddFee';
import { Fee, HourlyRate, LumpSum, Retainer, Text } from '@/interfaces/fees';
import { FEE_ACTIONS } from '../../reducers/feeReducer';
import HourlyRateTable from './HourlyRateTable';
import LumpSumTable from './LumpSumTable';
import { IconTrash, IconX, IconGripVertical } from '@tabler/icons-react';
import FeeSectionWrapper from './FeeSectionWrapper';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import ExpandingTextArea from '@/components/ExpandingTextArea';

export default function FeesSection({ fees }: { fees: Fee[] }) {
  const { t } = useTranslation();
  const { dispatchProposal } = useProposal();

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = fees.findIndex((fee) => fee.id === active.id);
    const newIndex = fees.findIndex((fee) => fee.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedFees = arrayMove(fees, oldIndex, newIndex);
    dispatchProposal({
      type: FEE_ACTIONS.REORDER_FEES,
      payload: reorderedFees,
    });
  };

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <SortableContext items={fees.map((fee) => fee.id)} strategy={verticalListSortingStrategy}>
        <Stack>
          <Group justify="space-between">
            <h2>{t('fees')}</h2>
            <AddFee />
          </Group>
          {/* <Stack w="80%" align="center"> */}
          {fees.map((fee, index) => {
            const renderFeeComponent = () => {
              switch (fee.type) {
                case 'HourlyRate':
                  return <HourlyRateComponent key={index} fee={fee} index={index} />;
                case 'LumpSum':
                  return <LumpSumComponent key={index} fee={fee} index={index} />;
                // case 'BlendedRate':
                //   return <BlendedRateComponent key={index} fee={fee} index={index} />;
                case 'Retainer':
                  return <RetainerComponent key={index} fee={fee} index={index} />;
                case 'Text':
                  return <TextComponent key={index} fee={fee} index={index} />;
                default:
                  return null;
              }
            };

            return renderFeeComponent();
          })}
          {/* </Stack> */}
        </Stack>
      </SortableContext>
    </DndContext>
  );
}

function HourlyRateComponent({ fee, index }: { fee: HourlyRate; index: number }) {
  const { t } = useTranslation();

  return (
    <FeeSectionWrapper title={t('hourlyRate')} index={index} fee={fee}>
      <HourlyRateTable fee={fee} index={index} />
    </FeeSectionWrapper>
  );
}

function LumpSumComponent({ fee, index }: { fee: LumpSum; index: number }) {
  const { t } = useTranslation();

  return (
    <FeeSectionWrapper title={t('lumpSum')} index={index} fee={fee}>
      <LumpSumTable fee={fee} index={index} />
    </FeeSectionWrapper>
  );
}

function RetainerComponent({ fee, index }: { fee: Retainer; index: number }) {
  const { t } = useTranslation();
  const { dispatchProposal } = useProposal();

  const handleUpdate = (newFee: Retainer) => {
    dispatchProposal({
      type: FEE_ACTIONS.UPDATE_FEE,
      index,
      payload: newFee,
    });
  };

  return (
    <FeeSectionWrapper title={t('retainer')} index={index} fee={fee}>
      <Group ml="lg">
        <NumberInput
          label={t('amount')}
          value={fee.amount}
          onChange={(value) => handleUpdate({ ...fee, amount: Number(value) })}
        />
        <Select
          label={t('period')}
          data={[
            { label: t('monthly'), value: 'Monthly' },
            { label: t('quarterly'), value: 'Quarterly' },
            { label: t('yearly'), value: 'Yearly' },
          ]}
          value={fee.period}
          onChange={(value) => handleUpdate({ ...fee, period: value || 'Monthly' })}
        />
      </Group>
    </FeeSectionWrapper>
  );
}

// function BlendedRateComponent({ fee, index }: { fee: BlendedRate; index: number }) {
//   const { t } = useTranslation();
//   const { dispatchProposal } = useProposal();

//   const handleUpdate = (newFee: BlendedRate) => {
//     dispatchProposal({
//       type: FEE_ACTIONS.UPDATE_FEE,
//       index,
//       payload: newFee,
//     });
//   };

//   return (
//     <FeeSectionWrapper title={t('blendedRate')} index={index} fee={fee}>
//       <NumberInput
//         label={t('rate')}
//         value={fee.rate}
//         onChange={(value) => handleUpdate({ ...fee, rate: Number(value) })}
//         maw={'10rem'}
//         ml={'lg'}
//       />
//     </FeeSectionWrapper>
//   );
// }

function TextComponent({ fee, index }: { fee: Text; index: number }) {
  const { t } = useTranslation();
  const { dispatchProposal } = useProposal();
  const { setNodeRef, transform, transition, isDragging, listeners, attributes } = useSortable({
    id: fee.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  const handleUpdate = (newFee: Text) => {
    dispatchProposal({
      type: FEE_ACTIONS.UPDATE_FEE,
      index,
      payload: newFee,
    });
  };

  const handleDelete = () => {
    dispatchProposal({
      type: FEE_ACTIONS.REMOVE_FEE,
      index,
    });
  };

  return (
    <Paper withBorder radius="md" shadow="sm" p="md" ref={setNodeRef} style={style}>
      <Group justify="space-between">
        <Group gap="xs">
          <div style={{ cursor: 'grab', padding: '0.5rem' }} {...listeners} {...attributes}>
            <IconGripVertical size={20} />
          </div>
          <Title order={4} fw={700}>
            {t('text')}
          </Title>
        </Group>
        <ActionIcon variant="subtle" color="red" onClick={handleDelete} aria-label={t('delete')}>
          <IconX size={20} />
        </ActionIcon>
      </Group>
      <Stack align="stretch" w="100%">
        <ExpandingTextArea
          label={t('title')}
          value={fee.title}
          onChange={(e) => handleUpdate({ ...fee, title: e.target.value })}
          placeholder={''}
          min_max_rows={1}
        />
        <ExpandingTextArea
          label={t('text')}
          value={fee.text}
          onChange={(e) => handleUpdate({ ...fee, text: e.target.value })}
          placeholder={t('feeTextDescription')}
        />
      </Stack>
    </Paper>
  );
}
